import { Button, Col, Flex, Form, Input, Row, Select, Tooltip, Typography } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Controller } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useTranslation } from 'react-i18next'
import { ResetIcon, ResetIconDisabled } from 'src/assets/icons'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'

import './index.scss'
import { ChangeEvent } from 'react'
import { inputNumber } from 'src/app/shared/utils'

interface Props {
  errors: any
  control: any
  dirtyFields?: any
  fileContainsIssuedDate: boolean
  dateInFileDontIncludeSeparators: boolean
  trigger?: any
  getValues: any
  resetMappingFields?: () => void
  disabled?: boolean
  enableResetBtn?: boolean
  setIsChanged?: (e: boolean) => void
  resetDateFormat?: () => void
}

function MappingFieldsTxt(props: Readonly<Props>) {
  const { t } = useTranslation()

  const lookupValues = useSelector(getLookupValues)

  const { DateFormat, DateFormatWithSeparator } = lookupValues || {}

  const {
    errors,
    control,
    fileContainsIssuedDate,
    dateInFileDontIncludeSeparators,
    trigger,
    resetMappingFields,
    dirtyFields,
    disabled = false,
    enableResetBtn = false,
    setIsChanged,
    resetDateFormat,
  } = props

  const mappingFields = [
    'issuedDateFormat',
    'dateInFileDontIncludeSeparators',
    'checkNumberFrom',
    'amountFrom',
    'issuedDateFrom',
    'accountNumberFrom',
    'issuedPayeeFrom',
    'recordTypeFrom',
    'checkNumberTo',
    'amountTo',
    'issuedDateTo',
    'accountNumberTo',
    'issuedPayeeTo',
    'recordTypeTo',
  ]

  const isResetDisabled =
    dirtyFields && !Object.keys(dirtyFields)?.some((item) => mappingFields?.includes(item)) && !enableResetBtn

  return (
    <>
      <Flex className='mapping-fields-header'>
        <Typography className='mapping-fields-title'>
          {t('FileMappingSetup:addFileMapping.title.mappingFields')}
        </Typography>

        <Button
          className='reset-button'
          onClick={() => {
            resetMappingFields?.()
          }}
          disabled={disabled || isResetDisabled}
        >
          {isResetDisabled ? <ResetIconDisabled /> : <ResetIcon />}
          {t('OverallFunctionality:reset_btn')}
        </Button>
      </Flex>
      <Flex vertical className='mapping-fields-container'>
        <Row gutter={24} align='middle'>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.checkNumber')} className='isRequired'>
              <Controller
                name='checkNumberFrom'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('checkNumberTo')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='checkNumberFrom' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.checkNumber')} className='isRequired opacity-0'>
              <Controller
                name='checkNumberTo'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('checkNumberFrom')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='checkNumberTo' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.amount')} className='isRequired'>
              <Controller
                name='amountFrom'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('amountTo')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='amountFrom' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.amount')} className='isRequired opacity-0'>
              <Controller
                name='amountTo'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('amountFrom')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='amountTo' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} align='middle'>
          {fileContainsIssuedDate && (
            <>
              <Col span={12}>
                <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedDate')} className='isRequired'>
                  <Controller
                    name='issuedDateFrom'
                    control={control}
                    disabled={disabled}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                        onChange={(e) => {
                          field.onChange(inputNumber(e), { shouldValidate: true })
                          trigger('issuedDateTo')
                          setIsChanged && setIsChanged(true)
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='issuedDateFrom' />
                  </Typography>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('FileMappingSetup:addFileMapping.label.issuedDate')}
                  className='isRequired opacity-0'
                >
                  <Controller
                    name='issuedDateTo'
                    control={control}
                    disabled={disabled}
                    render={({ field, fieldState }) => (
                      <Input
                        {...field}
                        status={!!fieldState.error ? 'error' : ''}
                        placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                        onChange={(e) => {
                          field.onChange(inputNumber(e), { shouldValidate: true })
                          trigger('issuedDateFrom')
                          setIsChanged && setIsChanged(true)
                        }}
                      />
                    )}
                  />
                  <Typography className='error-message'>
                    <ErrorMessage errors={errors} name='issuedDateTo' />
                  </Typography>
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Row gutter={24} align='middle'>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.datesInFile')} className='isRequired'>
              <Controller
                name='dateInFileDontIncludeSeparators'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    onChange={(e) => {
                      field.onChange(e)
                      resetDateFormat?.()
                      setIsChanged && setIsChanged(true)
                    }}
                    options={[
                      { value: false, label: 'No' },
                      { value: true, label: 'Yes' },
                    ]}
                    disabled={disabled || !fileContainsIssuedDate}
                  />
                )}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.datesInFile')} className='isRequired opacity-0'>
              <Controller
                name='issuedDateFormat'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Select
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.issuedDateFormat')}
                    options={dateInFileDontIncludeSeparators ? DateFormatWithSeparator : DateFormat}
                    onChange={(e) => {
                      field.onChange(e)
                      setIsChanged && setIsChanged(true)
                    }}
                    disabled={disabled || !fileContainsIssuedDate} //|| !dateInFileDontIncludeSeparators
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='issuedDateFormat' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={
                <Tooltip
                  placement='right'
                  title={t('FileMappingSetup:addFileMapping.tooltip.accountNumber')}
                  color='white'
                  trigger='hover'
                  arrow={false}
                >
                  <Flex gap={9}>
                    {t('FileMappingSetup:addFileMapping.label.accountNumber')}
                    <ExclamationCircleOutlined />
                  </Flex>
                </Tooltip>
              }
            >
              <Controller
                name='accountNumberFrom'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('accountNumberTo')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='accountNumberFrom' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <Tooltip
                  placement='right'
                  title={t('FileMappingSetup:addFileMapping.tooltip.accountNumber')}
                  color='white'
                  trigger='hover'
                  arrow={false}
                >
                  <Flex gap={9}>
                    {t('FileMappingSetup:addFileMapping.label.accountNumber')}
                    <ExclamationCircleOutlined />
                  </Flex>
                </Tooltip>
              }
              className='opacity-0'
            >
              <Controller
                name='accountNumberTo'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('accountNumberFrom')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='accountNumberTo' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedPayee')}>
              <Controller
                name='issuedPayeeFrom'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('issuedPayeeTo')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='issuedPayeeFrom' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.issuedPayee')} className='opacity-0'>
              <Controller
                name='issuedPayeeTo'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('issuedPayeeFrom')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='issuedPayeeTo' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.recordType')}>
              <Controller
                name='recordTypeFrom'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.from')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('recordTypeTo')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='recordTypeFrom' />
              </Typography>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('FileMappingSetup:addFileMapping.label.recordType')} className='opacity-0'>
              <Controller
                name='recordTypeTo'
                control={control}
                disabled={disabled}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    status={!!fieldState.error ? 'error' : ''}
                    placeholder={disabled ? '' : t('FileMappingSetup:addFileMapping.placeholder.to')}
                    onChange={(e) => {
                      field.onChange(inputNumber(e), { shouldValidate: true })
                      trigger('recordTypeFrom')
                      setIsChanged && setIsChanged(true)
                    }}
                  />
                )}
              />
              <Typography className='error-message'>
                <ErrorMessage errors={errors} name='recordTypeTo' />
              </Typography>
            </Form.Item>
          </Col>
        </Row>
      </Flex>
    </>
  )
}

export default MappingFieldsTxt
